import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createMigrate } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { useDispatch } from 'react-redux';

import userReducer from './slices/userSlice';
import tileBuilderReducer from './slices/tileBuilderSlice';
import salesforceOpportunitiesReducer from './slices/salesforceOpportunitiesSlice';
import productsReducer from './slices/productsSlice';
import toolboxReducer from './slices/toolboxSlice';
import salesforcePermissionsReducer from './slices/salesforcePermissionsSlice';
import uiReducer from './slices/uiSlice';
import salesforceCustomersReducer from './slices/salesforceCustomersSlice';
import salesforceSalesLevelReducer from './slices/salesforceSalesLevelSlice';
import salesforceOptionsReducer from './slices/salesforceOptionsSlice';
import approvalTemplatesReducer from './slices/approvalTemplatesSlice';
import moodleReducer from './slices/moodleSlice';
import gtmSolutionsReducer from './slices/gtmSolutionsSlice';
import journeyMapsReducer from './slices/journeyMapsSlice';
import journeyMapBuilderReducer from './slices/journeyMapBuilderSlice';
import registryReducer from './slices/registrySlice';

const userPersistConfig = {
  key: 'user',
  storage,
  version: 3,
  // Bearer tokens ARE persisted so a page reload keeps the session intact. Without this the
  // toolboxToken/sfToken rehydrate as null on every reload, and useAuthenticateUser's broken-state
  // sanity check (both tokens absent → clearLocalSession) fired before the async silent restore
  // could re-mint them — logging the user out immediately on reload. Stale-token risk is bounded:
  // getToolboxToken()/firstUsableToken in queriesHelpers.ts skip a JWT whose exp has passed
  // (isExpiredJwt), and the proactive-expiry effect starts recovery on a spent token.
  blacklist: ['uiModules'],
  migrate: createMigrate(
    {
      1: (state: any) => state,
      2: (state: any) => state,
      3: (state: any) => state,
    },
    { debug: false }
  ),
};

// update the version number so that the migration function will run,
// and increment the migration step if all you need is to force the slice to reload
const productsPersistConfig = {
  key: 'products',
  storage,
  version: 1,
  migrate: createMigrate(
    {
      1: () => undefined,
    },
    { debug: false }
  ),
};

const salesforcePermissionsPersistConfig = {
  key: 'salesforcePermissions',
  storage,
  version: 1,
  migrate: createMigrate(
    {
      1: (state: any) => state
    },
    { debug: false }
  ),
};

const moodlePersistConfig = {
  key: 'moodle',
  storage,
  version: 1,
  migrate: createMigrate(
    {
      1: (state: any) => state
    },
    { debug: false }
  ),
};

const rootReducer = combineReducers({
  toolbox: toolboxReducer,
  salesforceOpportunities: salesforceOpportunitiesReducer,
  salesforceCustomers: salesforceCustomersReducer,
  salesforceOptions: salesforceOptionsReducer,
  salesforcePermissions: persistReducer(salesforcePermissionsPersistConfig, salesforcePermissionsReducer),
  salesforceSalesLevels: salesforceSalesLevelReducer,
  user: persistReducer(userPersistConfig, userReducer),
  tileBuilder: tileBuilderReducer,
  products: persistReducer(productsPersistConfig, productsReducer),
  gtmSolutions: gtmSolutionsReducer,
  ui: uiReducer,
  approvalTemplates: approvalTemplatesReducer,
  moodle: persistReducer(moodlePersistConfig, moodleReducer),
  journeyMaps: journeyMapsReducer,
  journeyMapBuilder: journeyMapBuilderReducer,
  registry: registryReducer
});

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export const persistor = persistStore(store);

// Cleanup old persisted state on app load - Added 03/19 - can remove in a few weeks after all users have updated
const LEGACY_ROOT_PURGED_FLAG = 'legacy_root_purged';
if (!localStorage.getItem(LEGACY_ROOT_PURGED_FLAG)) {
  localStorage.removeItem('persist:root');
  localStorage.setItem(LEGACY_ROOT_PURGED_FLAG, '1');
}

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export { store };